import {
    List,
    NumberField,
    DatagridConfigurable,
    SelectColumnsButton,
    TopToolbar,
    CreateButton,
    TextInput,
    FilterList,
    FilterListItem,
    useRecordContext,
    Pagination,
    FunctionField,
} from 'react-admin';
import { Card, CardContent, Typography, Tooltip, Chip } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import CachedIcon from '@mui/icons-material/Cached';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import InventoryIcon from '@mui/icons-material/Inventory';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LoginIcon from '@mui/icons-material/Login';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import GroupsIcon from '@mui/icons-material/Groups';
import BoltIcon from '@mui/icons-material/Bolt';

const ValkeyListAside = () => (
    <Card
        sx={{
            order: -1,
            flex: '0 0 15em',
            mr: 2,
            mt: 4.5,
            alignSelf: 'flex-start',
            display: { xs: 'none', md: 'block' },
        }}
    >
        <CardContent sx={{ pt: 1 }}>
            <FilterList label="Kategoria" icon={<CategoryIcon />}>
                <FilterListItem
                    label="Kaikki"
                    value={{ pattern: '*' }}
                    icon={<StorageIcon />}
                />
                <FilterListItem
                    label="Sessiot"
                    value={{ pattern: 'auth_*' }}
                    icon={<PersonIcon />}
                />
                <FilterListItem
                    label="QueryCache"
                    value={{ pattern: 'query:*' }}
                    icon={<CachedIcon />}
                />
                <FilterListItem
                    label="ResponseCache"
                    value={{ pattern: 'respcache_*' }}
                    icon={<BoltIcon />}
                />
                <FilterListItem
                    label="OpenSearch"
                    value={{ pattern: 'opensearch:*' }}
                    icon={<SearchIcon />}
                />
                <FilterListItem
                    label="Ratelimit"
                    value={{ pattern: 'ratelimit_*' }}
                    icon={<BlockIcon />}
                />
                <FilterListItem
                    label="TuoteCache"
                    value={{ pattern: 'all_*' }}
                    icon={<InventoryIcon />}
                />
                <FilterListItem
                    label="Opettaja fulldata"
                    value={{ pattern: 'teacher_full_data_*' }}
                    icon={<SchoolIcon />}
                />
                <FilterListItem
                    label="Aktivointi"
                    value={{ pattern: 'activate_*' }}
                    icon={<VerifiedUserIcon />}
                />
                <FilterListItem
                    label="OIDC-koodivaihto"
                    value={{ pattern: 'code_*' }}
                    icon={<VpnKeyIcon />}
                />
                <FilterListItem
                    label="Materiaalit"
                    value={{ pattern: 'materials_*' }}
                    icon={<LibraryBooksIcon />}
                />
                <FilterListItem
                    label="MPASSid"
                    value={{ pattern: 'mpass_*' }}
                    icon={<LoginIcon />}
                />
                <FilterListItem
                    label="Opisk. lisenssit"
                    value={{ pattern: 'opiskelija_getlicense_*' }}
                    icon={<CardMembershipIcon />}
                />
                <FilterListItem
                    label="DAU"
                    value={{ pattern: 'dau:*' }}
                    icon={<GroupsIcon />}
                />
                <FilterListItem
                    label="Muut"
                    value={{ pattern: '__other__' }}
                    icon={<StorageIcon />}
                />
            </FilterList>
        </CardContent>
    </Card>
);

const KEY_MAX_LENGTH = 110;

const TruncatedKeyField = ({ source }: { source: string; label?: string }) => {
    const record = useRecordContext();
    if (!record) return null;
    const full = record[source] as string;
    if (full.length <= KEY_MAX_LENGTH) {
        return <Typography variant="body2">{full}</Typography>;
    }
    return (
        <Tooltip title={full} arrow>
            <Typography variant="body2" sx={{ cursor: 'default' }}>
                {full.substring(0, KEY_MAX_LENGTH)}…
            </Typography>
        </Tooltip>
    );
};

const valkeyFilters = [<TextInput source="pattern" alwaysOn resettable />];

const StorageTypeField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const type = record.type as string;
    const label =
        type === 'json' ? 'JSON' : type === 'binary' ? 'BINARY' : 'STRING';
    const color =
        type === 'json' ? 'info' : type === 'binary' ? 'secondary' : 'success';
    return <Chip label={label} size="small" color={color} variant="outlined" />;
};

const ListActions = () => (
    <TopToolbar>
        <SelectColumnsButton />
        <CreateButton />
    </TopToolbar>
);

const ValkeyList = ({ resource }: { resource?: string }) => {
    return (
        <List
            resource={resource}
            {...(resource
                ? {
                      storeKey: 'valkeyListEmbedded',
                      disableSyncWithLocation: true,
                  }
                : {})}
            filters={valkeyFilters}
            filterDefaultValues={{ pattern: '*' }}
            sort={{ field: 'key', order: 'ASC' }}
            perPage={50}
            pagination={<Pagination rowsPerPageOptions={[25, 50, 100, 200]} />}
            actions={<ListActions />}
            aside={<ValkeyListAside />}
        >
            <DatagridConfigurable rowClick="show">
                <TruncatedKeyField
                    source="key"
                    label="resources.valkey.fields.key"
                />
                <FunctionField
                    source="type"
                    label="resources.valkey.fields.type"
                    render={() => <StorageTypeField />}
                />
                <NumberField source="ttl" label="resources.valkey.fields.ttl" />
            </DatagridConfigurable>
        </List>
    );
};

export default ValkeyList;
